import { makeStyles, useTheme } from '@mui/styles'
import clsx from 'clsx'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import config from '@/config'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { isAndroid } from '@/modules/_helpers/detectMobile'
import IconAsLink from '@/modules/base/components/icon-as-link/icon-as-link'

import { user } from 'ampersand-app'
import { Outlet, useNavigate } from 'react-router'
import { isMeinBuero } from '@/utils/build-env'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import { isNonReturnableRoute } from '@/utils/post-login-redirect'
import { brandFontScope, brandText } from '@/utils/brand-typography'

import IconSyncDesktop from '@assets/images/icons/sync_desktop.svg'
import IconSatellit from '@assets/images/icons/satellit.svg'

const { agbUrl, imprintUrl, privacyUrl, customerSupport, remoteAssistance } = config

const isMB = isMeinBuero()

const useStyles = makeStyles((theme: ThemeInterface) => ({
  iframe: {
    height: '100%',
    border: 'none',
    [theme.breakpoints.down(1024)]: {
      display: 'none',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    flex: 1,
    [theme.breakpoints.down(1024)]: {
      flexDirection: 'column',
      overflowY: 'auto',
    },
  },
  contentContainer: ({ isAndroid }: { isAndroid: boolean }) => {
    return {
      width: 500,
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflowY: 'auto',
      [theme.breakpoints.down(1024)]: {
        width: '100%',
        minWidth: 358,
        minHeight: isAndroid ? '-webkit-fill-available' : null, // only for login,
        '.registration &': {
          minHeight: 'auto',
        },
        overflowY: 'unset',
      },
      [theme.breakpoints.up(1024)]: {
        minHeight: isAndroid ? '-webkit-fill-available' : null,
        '.registration &': {
          minHeight: isAndroid ? '-webkit-fill-available' : null,
        },
        overflowY: 'unset',
      },
    }
  },
  footer: {
    bottom: 0,
    color: theme.primary,
    display: 'flex',
    fontSize: theme.fontSizes.smaller,
    lineHeight: 1.07,
    letterSpacing: '0.12px',
    paddingBottom: '30px',
    width: '100%',
    padding: '0px 53px',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.devices.mobile]: {
      padding: '0px 23px',
      paddingBottom: '30px',
    },
  },
  sidebar: {
    width: '75%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down(1024)]: {
      display: 'none',
      width: '100%',
      minHeight: '-webkit-fill-available',
      maxWidth: 'unset',
      overflowY: 'hidden',
    },
  },
  // orgaMAX (new brand design): the form column next to the visual, sized by the window width (DO-13587):
  // from 1440 fixed, 1024-1439 relative, 768-1023 without the visual, below 768 the phone layout.
  // Logo, form and footer share the width of the form (468).
  // Doubled class: contentContainer is a dynamic rule, which is added after the static ones.
  brandContent: {
    '&&': {
      alignItems: 'flex-start',
      padding: '48px 64px 40px',
      '& > *': {
        width: '100%',
        maxWidth: 468,
      },
      [theme.breakpoints.up(1440)]: {
        flex: '0 0 596px',
        width: 596,
      },
      [theme.breakpoints.between(1024, 1440)]: {
        flex: '0 0 41%',
        width: '41%',
        minWidth: 480,
        maxWidth: 596,
        padding: '48px 48px 40px',
      },
      [theme.breakpoints.down(1024)]: {
        width: '100%',
        minWidth: 0,
        alignItems: 'center',
        padding: '48px 24px 40px',
      },
      [theme.breakpoints.down(768)]: {
        alignItems: 'stretch',
        padding: '0 24px',
        '& > *': {
          maxWidth: 'none',
        },
      },
    },
  },
  // a page without the visual keeps the form centred, like below 1024
  brandContentWithoutPanel: {
    '&&&': {
      [theme.breakpoints.up(1024)]: {
        flex: 1,
        width: '100%',
        maxWidth: 'none',
        alignItems: 'center',
      },
    },
  },
  // the visual takes the rest of the width, in a rounded card with some space around it
  brandSidebar: {
    flex: 1,
    width: 'auto',
    minWidth: 0,
    margin: 16,
    borderRadius: 24,
    overflow: 'hidden',
  },
  brandFooter: {
    justifyContent: 'flex-start',
    gap: 12,
    padding: '24px 0 0',
    color: theme.palette.brand.text.secondary,
    ...brandFontScope(theme),
    ...brandText(theme, 'small'),
    '& a': {
      color: 'inherit',
    },
    // phone: the links wrap, above the Safari bar and the home indicator
    [theme.breakpoints.down(768)]: {
      flexWrap: 'wrap',
      gap: 16,
      padding: '24px 0 calc(24px + env(safe-area-inset-bottom))',
    },
  },
  brandSeperator: {
    width: 1,
    height: 16,
    backgroundColor: theme.palette.brand.divider,
    [theme.breakpoints.down(768)]: {
      display: 'none',
    },
  },
  footerLink: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  seperator: {
    color: theme.primary,
    display: 'inline-block',
    padding: '0 10px',
  },
}))

export type PublicAccessContextType = {
  sidebarUrl?: string
  setSidebarUrl?: (url: string) => void
  isLoading?: boolean
  setIsLoading?: (loading: boolean) => void
  hideSidebar?: boolean
  setHideSidebar?: (hide: boolean) => void
}

const PublicAccessLayout = () => {
  const classes = useStyles({ isAndroid: isAndroid() })
  const theme = useTheme<ThemeInterface>()

  const [sidebarUrl, setSidebarUrl] = useState<string>(null)
  const [hideSidebar, setHideSidebar] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const wasLoggedInRef = useRef(Boolean(user?.loggedIn))

  const contextValue = useMemo<PublicAccessContextType>(() => ({
    sidebarUrl,
    setSidebarUrl: (newValue) => newValue !== sidebarUrl ? setSidebarUrl(newValue) : null,
    isLoading,
    setIsLoading,
    hideSidebar,
    setHideSidebar,
  }), [sidebarUrl, isLoading, hideSidebar])

  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current
    wasLoggedInRef.current = Boolean(user?.loggedIn)

    if (user?.loggedIn && !user?.loginInProgress) {
      if (!wasLoggedIn) {
        return
      }

      const returnAfterLogin = WebStorageService.getItem(WebStorageKey.RETURN_AFTER_LOGIN)
      if (!isNonReturnableRoute(returnAfterLogin)) {
        navigate(returnAfterLogin, { replace: true })
        return
      }

      navigate('/dashboard', { replace: true })
    }
  }, [user?.loggedIn, user?.loginInProgress, navigate])

  return (
    <div className={classes.root}>
      <main className={clsx(classes.contentContainer, !isMB && classes.brandContent, !isMB && hideSidebar && classes.brandContentWithoutPanel)}>
        <Outlet context={contextValue} />
        {!isLoading && !isMB && (
          // orgaMAX: the trust badges are part of the visual on the right now
          <footer className={clsx(classes.footer, classes.brandFooter)}>
            <a href={imprintUrl} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>{lang.imprint}</a>
            <span className={classes.brandSeperator} aria-hidden="true" />
            <span>
              <a href={agbUrl} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>{lang.termsOfConditons}</a>
              &nbsp;&&nbsp;
              <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>{lang.privacy}</a>
            </span>
            <span className={classes.brandSeperator} aria-hidden="true" />
            <a href={customerSupport} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>{lang.customerSupport}</a>
            {/* not in the design, but the remote assistance stays available */}
            <span className={classes.brandSeperator} aria-hidden="true" />
            <RichTooltip placement='top' content={<small>{lang.userSupportLabel}</small>}>
              <div>
                <IconAsLink icon={IconSyncDesktop} width={20} height={20} fillColor={theme.palette.brand.text.secondary} linkClass={classes.footerLink} href={remoteAssistance} />
              </div>
            </RichTooltip>
          </footer>
        )}
        {!isLoading && isMB && (
          <footer className={classes.footer}>
            <a href={imprintUrl} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>{lang.imprint}</a>
            <span className={classes.seperator}>|</span>
            <a href={agbUrl} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>
              {lang.termsOfConditons}
            </a>&nbsp;&&nbsp;<a href={privacyUrl} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>
              {lang.privacy}
            </a>
            <span className={classes.seperator}>|</span>
            <a href={customerSupport} target="_blank" rel="noopener noreferrer" className={classes.footerLink}>{lang.customerSupport}</a>
            <span className={classes.seperator}>|</span>
            <IconAsLink icon={IconSatellit} width={15} height={15} fillColor={theme.primary} linkClass={classes.footerLink} href={remoteAssistance} />
          </footer>
        )}
      </main>
      {
        !isMB && !hideSidebar && <aside className={clsx(classes.sidebar, classes.brandSidebar)}>
          {
            sidebarUrl && <iframe className={classes.iframe} height='100%' src={encodeURI(sidebarUrl)}></iframe>
          }
        </aside>
      }
    </div>
  )
}

export default PublicAccessLayout
